import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  FilterList,
  AllInclusive,
  Pending,
  Paid,
  LocalShipping,
  Warning,
} from '@mui/icons-material';
import type { SavedFilterView } from '../hooks/useSavedFilterViews';

interface SavedViewsTabsProps {
  views: SavedFilterView[];
  activeViewId: string | null;
  onSelectView: (viewId: string | null) => void;
  onSaveCurrentView: () => void;
  onDeleteView: (viewId: string) => void;
  onRenameView: (viewId: string, newName: string) => void;
  showAllTab?: boolean;
  allTabLabel?: string;
}

const iconMap: Record<string, React.ReactNode> = {
  all: <AllInclusive fontSize="small" />,
  pending: <Pending fontSize="small" />,
  paid: <Paid fontSize="small" />,
  delivered: <LocalShipping fontSize="small" />,
  overdue: <Warning fontSize="small" />,
  filter: <FilterList fontSize="small" />,
};

export const SavedViewsTabs: React.FC<SavedViewsTabsProps> = ({
  views,
  activeViewId,
  onSelectView,
  onSaveCurrentView,
  onDeleteView,
  onRenameView,
  showAllTab = true,
  allTabLabel = 'Todas',
}) => {
  const [contextMenu, setContextMenu] = useState<{
    anchorEl: HTMLElement;
    viewId: string;
  } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    viewId: string;
    currentName: string;
  }>({ open: false, viewId: '', currentName: '' });
  const [newName, setNewName] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string | null) => {
    onSelectView(newValue);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, viewId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ anchorEl: event.currentTarget, viewId });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleRenameClick = () => {
    const view = views.find(v => v.id === contextMenu?.viewId);
    if (view) {
      setRenameDialog({
        open: true,
        viewId: view.id,
        currentName: view.name,
      });
      setNewName(view.name);
    }
    handleCloseContextMenu();
  };

  const handleDeleteClick = () => {
    if (contextMenu?.viewId) {
      onDeleteView(contextMenu.viewId);
    }
    handleCloseContextMenu();
  };

  const handleRenameConfirm = () => {
    if (renameDialog.viewId && newName.trim()) {
      onRenameView(renameDialog.viewId, newName.trim());
    }
    setRenameDialog({ open: false, viewId: '', currentName: '' });
    setNewName('');
  };

  const getViewIcon = (view: SavedFilterView) => {
    if (view.icon && iconMap[view.icon]) {
      return iconMap[view.icon];
    }
    return <FilterList fontSize="small" />;
  };

  // Determine tab value: null for "All", or the view id
  const currentTabValue = activeViewId ?? (showAllTab ? 'all' : views[0]?.id ?? false);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={currentTabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flexGrow: 1 }}
          >
            {showAllTab && (
              <Tab
                value="all"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AllInclusive fontSize="small" />
                    <span>{allTabLabel}</span>
                  </Box>
                }
                onClick={() => onSelectView(null)}
              />
            )}
            {views.map((view) => (
              <Tab
                key={view.id}
                value={view.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getViewIcon(view)}
                    <span>{view.name}</span>
                    {!view.isDefault && (
                      <IconButton
                        size="small"
                        sx={{ ml: 0.5, p: 0.25 }}
                        onClick={(e) => handleContextMenu(e, view.id)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Tooltip title="Guardar vista actual">
            <IconButton onClick={onSaveCurrentView} size="small" sx={{ ml: 1 }}>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Context menu for view actions */}
      <Menu
        anchorEl={contextMenu?.anchorEl}
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={handleRenameClick}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Renombrar
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      <Dialog
        open={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, viewId: '', currentName: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Renombrar vista</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre de la vista"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, viewId: '', currentName: '' })}>
            Cancelar
          </Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={!newName.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedViewsTabs;
